import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, X, FileWarning } from "lucide-react";

/**
 * DocumentViewer — full-screen, in-app viewer for images and PDFs.
 *
 * Fixes the Android TWA regression where AttachmentsSection.openFile()
 * used ``window.open("", "_blank") + document.write(<iframe>)`` — that
 * opened a Chrome Custom Tab without any way to return to the vehicle
 * page. The new viewer is a plain React modal:
 *   - top bar respecting env(safe-area-inset-top)
 *   - back arrow left (aria-label="Retour au véhicule")
 *   - close X right (aria-label="Fermer le document")
 *   - document name centered (falls back to a generic label)
 *   - <img> for images, <iframe> for PDFs (kept below the top bar so the
 *     close controls always stay tappable)
 *   - Android hardware back button: pushes an extra history entry on
 *     open, listens for popstate to close, cleans up on unmount.
 *   - Error state when ``src`` is missing.
 *
 * Props:
 *   open           : boolean — controls mount/unmount
 *   src            : data URL / http URL — the file to display
 *   mimeType       : "application/pdf" (iframe) or anything else (img)
 *   title          : displayed in the top bar, optional
 *   onClose()      : called by every close path (back arrow, X, ESC,
 *                    hardware back, backdrop click)
 *   backLabel      : override the aria-label of the back arrow
 *                    (default "Retour au véhicule")
 */
const Z_INDEX = 300;

export default function DocumentViewer({
  open,
  src,
  mimeType,
  title,
  onClose,
  backLabel = "Retour au véhicule",
}) {
  // Android hardware back: push a sentinel history entry when the viewer
  // opens so the FIRST back closes the modal instead of leaving the app.
  // Guarded so multiple opens don't stack sentinels.
  useEffect(() => {
    if (!open) return undefined;
    const sentinel = { __mfDocViewer: true, ts: Date.now() };
    try {
      window.history.pushState(sentinel, "");
    } catch { /* SSR / hardened iframe — no-op */ }
    const onPop = () => { onClose?.(); };
    window.addEventListener("popstate", onPop);

    const onKey = (e) => {
      if (e.key === "Escape") { onClose?.(); }
    };
    document.addEventListener("keydown", onKey);

    // Prevent body scroll while open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      // If the sentinel is still on top of the history stack, pop it so
      // the app history doesn't grow after each open/close cycle.
      try {
        if (window.history.state && window.history.state.__mfDocViewer) {
          window.history.back();
        }
      } catch { /* no-op */ }
    };
  }, [open, onClose]);

  if (!open) return null;

  const isPdf = mimeType === "application/pdf";
  const displayName = (title && String(title).trim()) || (isPdf ? "Document PDF" : "Document");
  const hasSrc = !!src;

  // Portal to document.body so position:fixed escapes any transformed
  // ancestor (e.g. HistoryDetailSheet.mf-anim-in keeps a translateY(0)
  // after its entrance animation → creates a containing block that would
  // otherwise confine the viewer to the sheet's rectangle).
  const viewerNode = (
    <div
      data-testid="document-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={displayName}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.95)",
        display: "flex", flexDirection: "column",
        zIndex: Z_INDEX,
      }}
    >
      {/* Top bar — always visible, safe-area aware, above content */}
      <div
        style={{
          position: "sticky", top: 0, zIndex: 2,
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 10px)",
          paddingBottom: 10,
          paddingLeft: "calc(env(safe-area-inset-left, 0px) + 12px)",
          paddingRight: "calc(env(safe-area-inset-right, 0px) + 12px)",
          background: "rgba(11,25,44,0.92)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex", alignItems: "center", gap: 10,
          color: "#fff",
        }}
      >
        <button
          onClick={onClose}
          data-testid="document-viewer-back"
          aria-label={backLabel}
          style={{
            width: 44, height: 44, borderRadius: 22,
            background: "rgba(255,255,255,0.12)", border: 0,
            color: "#fff", cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={22} />
        </button>
        <div
          data-testid="document-viewer-title"
          style={{
            flex: 1, minWidth: 0,
            fontSize: 15, fontWeight: 700, color: "#fff",
            textAlign: "center",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {displayName}
        </div>
        <button
          onClick={onClose}
          data-testid="document-viewer-close"
          aria-label="Fermer le document"
          style={{
            width: 44, height: 44, borderRadius: 22,
            background: "rgba(255,255,255,0.12)", border: 0,
            color: "#fff", cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <X size={22} />
        </button>
      </div>

      {/* Body — grows to fill, safe-area aware on the bottom too */}
      <div
        style={{
          flex: 1, minHeight: 0,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          overflow: "auto",
        }}
      >
        {!hasSrc ? (
          <ErrorPanel onClose={onClose} />
        ) : isPdf ? (
          <iframe
            src={src}
            title={displayName}
            data-testid="document-viewer-pdf"
            style={{ border: 0, width: "100%", height: "100%", background: "#fff" }}
          />
        ) : (
          <img
            src={src}
            alt=""
            data-testid="document-viewer-image"
            style={{
              maxWidth: "100%", maxHeight: "100%",
              objectFit: "contain",
              userSelect: "none",
            }}
          />
        )}
      </div>
    </div>
  );

  // Guard against SSR / early render before body exists (unlikely in CRA
  // but safe): fall back to inline rendering only if document.body is
  // unavailable, which will never happen at runtime.
  if (typeof document === "undefined" || !document.body) return viewerNode;
  return createPortal(viewerNode, document.body);
}

function ErrorPanel({ onClose }) {
  return (
    <div
      data-testid="document-viewer-error"
      style={{
        padding: 24, textAlign: "center", color: "#fff",
        maxWidth: 360,
      }}
    >
      <div style={{
        width: 56, height: 56, borderRadius: 28,
        background: "rgba(239,68,68,0.20)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        marginBottom: 12,
      }}>
        <FileWarning size={26} color="#fca5a5" />
      </div>
      <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800 }}>
        Document indisponible
      </h2>
      <p style={{ margin: "0 0 18px", fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.5 }}>
        Ce document ne peut pas être affiché pour le moment.
      </p>
      <button
        onClick={onClose}
        data-testid="document-viewer-error-back"
        style={{
          padding: "12px 22px", borderRadius: 999,
          background: "#fff", color: "#0B192C",
          border: 0, cursor: "pointer",
          fontWeight: 700, fontSize: 15,
        }}
      >
        Revenir au véhicule
      </button>
    </div>
  );
}
